<script lang="ts">
  import { downloadLatestStockTakeXlsx } from "../actions/data";
  import { api } from "../api/client";
  import { mapUnique } from "../helpers/array";
  import { formatDayMonthYear, isLastNMonth, valueWithCurrency } from "../helpers/utils";

  import PillButton from "../components/buttons/PillButton.svelte";
  import InfoPanel from "../components/InfoPanel.svelte";
  import LoadingSpinner from "../components/LoadingSpinner.svelte";
  import Stack from "../components/Stack.svelte";
  import StockTakeInput from "../components/StockTakeInput.svelte";
  import StockValueCell from "../components/StockValueCell.svelte";
  import Text from "../components/Text.svelte";
  import SvelteTable, { type Column } from "../lib/SvelteTable.svelte";
  import PCell from "../components/cells/PCell.svelte";

  import { stockTake, store, stockTakeValues, lastTake } from "../helpers/store";

  import type {
    Buyer,
    StockTakeEntry,
    StockTakeEntryNullable,
    StockTakeResponse,
  } from "../helpers/types";
  import MultiSelect, { type ObjectOption } from "svelte-multiselect";
  import { selectedBuyer } from "../stores/buyer";

  let loading = true;
  var lastTakeUser: any;
  let boughtLast3months = true;
  let filterByName = "";

  let multiSelectValues: ObjectOption[] = [];
  let filterSelections: Record<string, ObjectOption[]> = {};

  let supplierOptions: ObjectOption[] = [];

  function updateSupplierOptions(suppliers: string[]) {
    supplierOptions = suppliers.map((value) => ({
      label: value,
      value: value.toLowerCase(),
    }));
  }

  $: {
    filterSelections["name"] = filterByName ? [{ label: filterByName, value: filterByName }] : [];
    filterSelections["supplier"] = multiSelectValues;
  }

  function getFilterData(data: StockTakeEntryNullable[], isBoughtLast3months: boolean) {
    if (!isBoughtLast3months) return data;

    return data.filter((item) => isLastNMonth(item.created_at, 3));
  }
  async function fetchStock(buyer: Buyer) {
    loading = true;
    stockTake.set([]);
    lastTake.set(null);
    lastTakeUser = null;
    let response: StockTakeResponse;

    try {
      response = await api
        .post("stock-take-get", {
          body: JSON.stringify({
            buyer_id: buyer.id,
          }),
        })
        .json();

      let entries = response.entries as StockTakeEntryNullable[];
      if (response.entries) {
        entries = response.entries.map((entry) => {
          return {
            ...entry,
            stock_take: entry.stock_take === 0 ? null : entry.stock_take,
          };
        });
      }
      const supplierNames = mapUnique(entries, (entry) => entry.supplier_name);
      updateSupplierOptions(supplierNames);
      stockTake.set(entries || []);
      lastTake.set(response.last_take);
      lastTakeUser = response.last_take_user;
    } catch (e) {
      console.warn(e);
      stockTake.set([]);
      lastTake.set(null);
    }

    loading = false;
  }

  $: if ($selectedBuyer) fetchStock($selectedBuyer);

  const COLUMNS: Record<string, Column<StockTakeEntryNullable>> = {
    supplier: {
      width: 15,
      key: "supplier",
      title: "Supplier",
      value: (v: StockTakeEntryNullable) => {
        return v.supplier_name.toLowerCase();
      },
      renderComponent: {
        component: PCell,
        propsf(v: StockTakeEntryNullable) {
          return {
            contents: v.supplier_name,
            centered: true,
          };
        },
      },
      multiFilter: true,
      filterValue: (v: StockTakeEntryNullable) => v.supplier_name.toLowerCase(),
    },
    reference: {
      width: 8,
      key: "reference",
      title: "Reference",
      renderComponent: {
        component: PCell,
        propsf(v: StockTakeEntryNullable) {
          return {
            contents: this.value!(v) || "",
          };
        },
      },
      value: (v: StockTakeEntryNullable) => v.reference,
    },
    name: {
      sortable: true,
      key: "name",
      title: "Name",
      value: (v: StockTakeEntryNullable) => v.item_name.toLowerCase(),
      renderComponent: {
        component: PCell,
        propsf(v: StockTakeEntryNullable) {
          return {
            contents: v.item_name,
          };
        },
      },
      search: true,
      searchValue: (v: StockTakeEntryNullable) => v.item_name.toLowerCase(),
    },
    last_price: {
      width: 5,
      sortable: true,
      key: "last_price",
      title: "Last price",
      value: (v: StockTakeEntryNullable) => Number(v.last_price),
      renderComponent: {
        component: PCell,
        propsf(v: StockTakeEntryNullable) {
          return {
            contents: v.last_price ? valueWithCurrency(Number(v.last_price), v.currency) : "--",
          };
        },
      },
    },

    current_price: {
      width: 5,
      sortable: true,
      key: "current_price",
      title: "Current price",
      value: (v: StockTakeEntryNullable) => Number(v.current_price),
      renderComponent: {
        component: PCell,
        propsf(v: StockTakeEntryNullable) {
          return {
            contents: v.current_price
              ? valueWithCurrency(Number(v.current_price), v.currency)
              : "--",
          };
        },
      },
    },
    take: {
      width: 5,
      key: "take",
      title: "Stock Take",
      value: (v: StockTakeEntryNullable) => {
        return v.stock_take;
      },
      renderComponent: { component: StockTakeInput, props: {} },
    },
    unit: {
      key: "unit",
      title: "Unit",
      value: (v: StockTakeEntryNullable) => v.item_units,
      renderComponent: {
        component: PCell,
        propsf(v: StockTakeEntryNullable) {
          return {
            contents: v.item_units,
          };
        },
      },
    },
    stock_value: {
      width: 8,
      key: "stock_value",
      title: "Stock Value",
      renderComponent: { component: StockValueCell, props: {} },
    },
  };

  $: cols = Object.values(COLUMNS);

  $: totalTake = $stockTake.reduce((acc, v) => {
    if ($stockTakeValues[v.item_id]) {
      return acc + Number(v.current_price) * $stockTakeValues[v.item_id];
    }

    return acc;
  }, 0);

  $: disabled = Object.keys($stockTakeValues).length === 0;

  async function onSubmit() {
    const items = $stockTake.reduce(
      (acc: StockTakeEntryNullable[], take: StockTakeEntryNullable) => {
        if ($stockTakeValues[take.item_id] && $stockTakeValues[take.item_id] > 0) {
          return [...acc, { ...take, stock_take: $stockTakeValues[take.item_id] }];
        }

        return acc;
      },
      []
    );

    const payload = {
      items,
      buyer_id: $selectedBuyer?.id,
    };

    try {
      await api
        .post("stock-take-set", {
          body: JSON.stringify(payload),
        })
        .json();
      window.location.reload();
    } catch (e) {
      alert("something went wrong.");
    }
  }

  $: lastTakeTotal = $lastTake
    ? $lastTake.items.reduce((acc: number, take: StockTakeEntry) => {
        return acc + take.stock_take * Number(take.current_price);
      }, 0)
    : 0;

  async function downloadCsv() {
    await downloadLatestStockTakeXlsx($selectedBuyer);
  }
</script>

<div class="container">
  <div class="header-bar-spaced">
    <Text size="large" bold style="padding-top: 24px;">stock management</Text>
    <div style="flex: 0; margin-bottom: 16px; margin-top: 24px;">
      <Stack horizontal justifyContent="space-between" alignItems="center">
        <Stack horizontal alignItems="center" spacing="medium" padding="0 0px">
          <InfoPanel
            headerTitle={"Last stock take"}
            title={$lastTake
              ? valueWithCurrency(
                  lastTakeTotal,
                  $stockTake.length > 0 ? $stockTake[0].currency : "GBP"
                )
              : "-"}
            onClick={downloadCsv}
            actionLabel={lastTake && "download last stock take (.xlsx)"}
            subtitle={lastTakeUser
              ? `${$lastTake?.items.length} item(s) recorded by ${lastTakeUser.first_name} ${
                  lastTakeUser.last_name
                } on ${formatDayMonthYear($lastTake?.created_at || "")}`
              : "no data"}
          />

          <InfoPanel
            headerTitle={"Current stock take"}
            title={valueWithCurrency(
              totalTake,
              $stockTake.length > 0 ? $stockTake[0].currency : "GBP"
            )}
            subtitle={`${Object.keys($stockTakeValues).length} items`}
          />
        </Stack>
      </Stack>
    </div>

    <div class="actions">
      <PillButton color={"accent"} title="submit stock take" onClick={onSubmit} {disabled} />
    </div>
  </div>

  {#if loading}
    <div class="loading-container">
      <LoadingSpinner size={60} />
    </div>
  {:else}
    <div class="wrapper">
      <div class="table-filters">
        <MultiSelect
          placeholder="select suppliers"
          bind:selected={multiSelectValues}
          options={supplierOptions}
        />
        <input
          class="table-input input-name"
          placeholder={`filter by name`}
          bind:value={filterByName}
        />
        <div class="padding">
          <input type="checkbox" bind:checked={boughtLast3months} />
          <Text inline ocd size="small">bought in last 3 months</Text>
        </div>
      </div>

      <div class="content">
        <div class="table">
          {#if $stockTake.length > 0}
            <SvelteTable
              columns={cols}
              rows={getFilterData($stockTake, boughtLast3months)}
              classNameRow="transaction-row"
              classNameTable="transactions-table"
              classNameCell="transaction-cell"
              sortBy="current_price"
              sortOrder={-1}
              bind:filterSelections
            />
          {:else}
            <div class="empty">
              <Text size="header" ocd color="neutral-03">no data for this buyer</Text>
            </div>
          {/if}
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .table-filters {
    display: flex;
    align-items: flex-start;
    background: var(--rekki-neutral-01);
    padding: 8px;
  }

  .container {
    flex-direction: column;
    background-color: white;
    height: 100vh;
    display: flex;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 75vh;
  }

  .empty {
    display: flex;
    flex: 1;
    height: 75vh;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-bottom: solid 1px var(--rekki-neutral-01);
  }

  .content {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: auto;
  }
  .wrapper {
    overflow-y: auto;
    margin-top: 8px;
    height: 100%;
    margin-left: 15px;
    margin-right: 15px;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 75vh;
  }

  .table {
    display: flex;
    background-color: white;
    flex: 1;
    flex-direction: column;
  }

  .actions {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-bottom: 16px;
  }

  .padding {
    padding: 7px;
  }

  .input-name {
    margin: 3px;
    margin-left: 15px;
    padding: 2px;
  }
</style>
